'use client';
import { useState, useEffect, useRef } from 'react';
import { components } from 'react-select';
import ReactDOM from 'react-dom';
import { useLanguage } from '@/entities/language/model/hooks';
import {
  Wrapper,
  Select,
  OptionCheck,
  OptionWrapper,
  ImageControl,
  ValueWrapper,
  ModalBackdrop,
  ModalContent,
} from './styles';
import { Image } from '@/shared/ui/image';
import { useChangeLocale, useCurrentLocale } from '@locales/client';
import { useUser } from '@/shared/providers/user-provider/use-user';
import { useProfile } from '@/features/profile/model/hooks';
import { withBreakpointsSlice } from '@/shared/providers/utils/with-breakpoints-slice';
import {
  MobileImageControl,
  MobileModalBackdrop,
  MobileModalContent,
  MobileOptionCheck,
  MobileOptionWrapper,
  MobileSelect,
} from '@/features/select-language/ui/mobile_styles';

const Option = ({ children, ...props }) => {
  const $isFirstChild = props.selectProps.options[0].code === props.data.code;
  const $isLastChild =
    props.selectProps.options[props.selectProps.options.length - 1].code ===
    props.data.code;

  return (
    <components.Option {...props}>
      <OptionWrapper
        $isSelected={props.isSelected}
        $isFirstChild={$isFirstChild}
        $isLastChild={$isLastChild}
      >
        <>
          <ImageControl
            alt="country"
            width={20}
            height={20}
            src={props.data.imageUrl}
          />
        </>
        <>{children}</>
        {props.isSelected && (
          <OptionCheck>
            <Image
              src={'/images/icons/check.svg'}
              width={16}
              height={16}
              style={{ display: 'flex' }}
              alt="Checkmark"
            />
          </OptionCheck>
        )}
      </OptionWrapper>
    </components.Option>
  );
};
const MobileOption = ({ children, ...props }) => {
  const $isFirstChild = props.selectProps.options[0].code === props.data.code;
  const $isLastChild =
    props.selectProps.options[props.selectProps.options.length - 1].code ===
    props.data.code;

  return (
    <components.Option {...props}>
      <MobileOptionWrapper
        $isSelected={props.isSelected}
        $isFirstChild={$isFirstChild}
        $isLastChild={$isLastChild}
      >
        <>
          <MobileImageControl
            alt="country"
            width={20}
            height={20}
            src={props.data.imageUrl}
          />
        </>
        <>{children}</>
        {props.isSelected && (
          <MobileOptionCheck>
            <Image
              src={'/images/icons/check.svg'}
              width={16}
              height={16}
              style={{ display: 'flex' }}
              alt="Checkmark"
            />
          </MobileOptionCheck>
        )}
      </MobileOptionWrapper>
    </components.Option>
  );
};

const DropdownIndicator = (props) => {
  const style = {
    transition: 'all .2s ease',
    display: 'flex',
    transform: props.selectProps.menuIsOpen ? 'rotate(0deg)' : 'rotate(180deg)',
  };

  return (
    <components.DropdownIndicator {...props}>
      <Image
        src="/images/icons/indicator-lang.svg"
        width={16}
        height={16}
        style={style}
        alt="indicator"
      />
    </components.DropdownIndicator>
  );
};

const Control = ({ children, ...props }) => {
  const currentOption = props.getValue()[0];
  return (
    <components.Control {...props}>
      <ImageControl
        alt="country"
        width={20}
        height={20}
        src={currentOption.imageUrl}
      />
      {children}
    </components.Control>
  );
};

const SelectLanguage = ({
  mobileLandscape = false,
  alignToLeft = false,
  isValueMode = false,
  inMobileMenu = false,
  authMode = false,
  $menuPlacement = 'auto',
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const { langauges, isLoading } = useLanguage();
  const [isFocused, setIsFocused] = useState(false)<Boolean>;
  const changeLocale = useChangeLocale({ preserveSearchParams: true });
  const { user, refreshUser } = useUser();
  const { trigger: profileTrigger } = useProfile();
  const locale = useCurrentLocale();
  const modalRootRef = useRef(null);

  useEffect(() => {
    if (!inMobileMenu) {
      let modalRoot = document.getElementById('modal-root');

      if (!modalRoot) {
        modalRoot = document.createElement('div');
        modalRoot.id = 'modal-root';
        document.body.appendChild(modalRoot);
      }

      modalRootRef.current = modalRoot;
    }

    return () => {
      const modalRoot = modalRootRef.current;
      if (!inMobileMenu && modalRoot && modalRoot.parentNode) {
        modalRoot.parentNode.removeChild(modalRoot);
      }
    };
  }, []);

  useEffect(() => {
    let originalStyle = '';

    if (isOpen && authMode) {
      originalStyle = document.body.style.pointerEvents;
      document.body.style.pointerEvents = 'auto';
    }

    return () => {
      if (isOpen && authMode) {
        document.body.style.pointerEvents = originalStyle;
      }
    };
  }, [isOpen, authMode]);

  useEffect(() => {
    if (user.id && user.lang !== locale) {
      handleLanguageChange(locale);
    }
  }, [locale, user.lang]);

  const handleLanguageChange = async (code) => {
    if (user.id && user.lang !== locale) {
      const formData = new FormData();
      formData.append('lang', code);

      await profileTrigger(formData);
      refreshUser();
    } else {
      changeLocale(code);
    }
  };

  if (isLoading) {
    return null;
  }
  const currentOption = langauges.find((langauge) => langauge.code === locale);

  const styles = {
    menu: (provided) => ({
      ...provided,
      position: 'absolute',
      [alignToLeft ? 'left' : 'right']: 0,
      ...(alignToLeft && {
        height: 'auto',
        overflowY: 'auto',
        maxHeight: 'none',
      }),
    }),
    menuList: (provided) => ({
      ...provided,
      ...(alignToLeft && {
        maxHeight: 'none',
      }),
    }),
    // control: (provided) => ({
    //   ...provided,
    //   width: '100px',
    // }),
    singleValue: (provided) => ({
      ...provided,
      display: 'inherit',
    }),
    valueContainer: (provided) => ({
      ...provided,
      display: 'flex',
      height: '20px',
    }),
  };
  const customStyles = {
    control: (base) => ({
      ...base,
      width: '100%',
      margin: 0,
      padding: 0,
      border: 'none',
      boxShadow: 'none',
      backgroundColor: 'transparent',
    }),
    menu: () => ({ display: 'none' }), // скрываем стандартное меню
  };
  const openModal = () => {
    setIsOpen(true);
  };
  const closeModal = () => {
    setIsOpen(false);
  };
  const handleChange = (option) => {
    handleLanguageChange(option.code);
    closeModal();
  };

  return (
    <Wrapper $isValueMode={isValueMode} $alignToLeft={alignToLeft}>
      {(!mobileLandscape || inMobileMenu) && (
        <Select
          styles={styles}
          $isFocused={isFocused}
          options={langauges}
          value={currentOption}
          unstyled
          delimiter={false}
          isSearchable={false}
          components={{
            DropdownIndicator,
            Control,
            Option,
            SingleValue: ConditionalSingleValue,
          }}
          selectProps={{ isValueMode }}
          getOptionLabel={(option) => option.title}
          getOptionValue={(option) => option.code}
          onFocus={() => setIsFocused(true)}
          isFocused={isFocused}
          maxMenuHeight={alignToLeft ? null : 300}
          onMenuClose={() => {
            setIsFocused(false);
          }}
          onMenuOpen={() => {
            setIsFocused(true);
          }}
          classNamePrefix={'language-select'}
          onChange={(option) => handleLanguageChange(option.code)}
          menuPlacement={$menuPlacement}
        />
      )}
      {mobileLandscape && !inMobileMenu && (
        <>
          <Select
            styles={customStyles}
            $isFocused={isFocused}
            options={langauges}
            value={currentOption}
            unstyled
            delimiter={false}
            isSearchable={false}
            components={{
              DropdownIndicator,
              Control,
              Option,
              SingleValue: ConditionalSingleValue,
            }}
            selectProps={{ isValueMode }}
            getOptionLabel={(option) => option.title}
            getOptionValue={(option) => option.code}
            onFocus={() => setIsFocused(true)}
            isFocused={isFocused}
            onMenuClose={() => {
              setIsFocused(false);
            }}
            onMenuOpen={() => {
              setIsFocused(true);
              openModal();
            }}
            classNamePrefix={'language-select'}
            onChange={(option) => {
              handleLanguageChange(option.code);
              closeModal();
            }}
          />

          {/* Портал для модального окна */}
          {isOpen &&
            ReactDOM.createPortal(
              <MobileModalBackdrop onClick={closeModal}>
                <MobileModalContent onClick={(e) => e.stopPropagation()}>
                  <MobileSelect
                    options={langauges}
                    value={currentOption}
                    classNamePrefix={'language-select'}
                    onChange={handleChange}
                    autoFocus
                    defaultMenuIsOpen
                    menuIsOpen
                    components={{
                      Option: MobileOption,
                      Control: () => null,
                      IndicatorSeparator: () => null,
                      DropdownIndicator: () => null,
                    }}
                    selectProps={{ isValueMode }}
                    getOptionLabel={(option) => option.title}
                    getOptionValue={(option) => option.code}
                    maxMenuHeight={'100vp'}
                    styles={{
                      control: (base) => ({ ...base, minWidth: 200 }),

                      container: (base) => ({ ...base, width: '100%' }),
                      menu: (base) => ({
                        ...base,
                        boxShadow: 'none',
                        position: 'relative',
                        border: 'none',
                        borderRadius: 0,
                        width: '100%',
                        maxWidth: '500px',
                        margin: '0 auto',
                      }),

                      option: (base, state) => ({
                        ...base,
                        backgroundColor: 'transparent',
                        padding: 0,
                      }),
                    }}
                  />
                </MobileModalContent>
              </MobileModalBackdrop>,
              modalRootRef.current
            )}
        </>
      )}
    </Wrapper>
  );
};

const ConditionalSingleValue = ({ data, selectProps }) => {
  const { isValueMode } = selectProps.selectProps;

  return (
    <ValueWrapper>
      {isValueMode ? data.code.toUpperCase() : data.title}
    </ValueWrapper>
  );
};

const SelectLanguageWithBreakpointsSlice = withBreakpointsSlice(
  SelectLanguage,
  ({ mobileLandscape }) => ({
    mobileLandscape,
  })
);
export { SelectLanguageWithBreakpointsSlice as SelectLanguage };
